import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Loader2,
  FileSignature,
  CreditCard,
} from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { useNavigate } from "@tanstack/react-router";
import { CONTRACT_TEMPLATES_META, isPremium, priceFor } from "@/lib/contract-templates";

import { listPublicCompanyAttachments } from "@/lib/api/company-assets.functions";
import { createContractOrder } from "@/lib/api/contract-orders.functions";

import { useQuery } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import { Disclaimer } from "@/components/site/Disclaimer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/contracts/$slug")({
  loader: ({ params }) => {
    const tpl = CONTRACT_TEMPLATES_META.find((t) => t.slug === params.slug);
    if (!tpl) throw notFound();
    return { tpl };
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `${loaderData?.tpl.title ?? "Contract"} — Personalise & download (PDF)`,
      },
      {
        name: "description",
        content: `Fill in client details and download a branded ${
          loaderData?.tpl.title ?? "contract"
        } PDF from Nkadimeng Danny Legal & Compliance Support — free.`,
      },
    ],
  }),
  notFoundComponent: () => (
    <div className="container-narrow py-24 text-center">
      <p className="text-muted-foreground">Contract not found.</p>
      <Link to="/contracts" className="mt-4 inline-block text-navy underline">
        Back to contract library
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container-narrow py-24 text-center">
      <p className="text-muted-foreground">
        Could not load this contract: {error.message}
      </p>
      <Link to="/contracts" className="mt-4 inline-block text-navy underline">
        Back to contract library
      </Link>
    </div>
  ),
  component: ContractFormPage,
});

type Vars = {
  partyA: { name: string; idnum: string; address: string; email: string; phone: string };
  partyB: { name: string; idnum: string; address: string; email: string; phone: string };
  amount: string;
  startDate: string;
  endDate: string;
  date: string;
  reference: string;
  cause: string;
  purpose: string;
  premises: string;
  about: string;
  skills: string;
  experience: string;
  education: string;
};

const empty = (): Vars => ({
  partyA: { name: "", idnum: "", address: "", email: "", phone: "" },
  partyB: { name: "", idnum: "", address: "", email: "", phone: "" },
  amount: "",
  startDate: "",
  endDate: "",
  date: "",
  reference: "",
  cause: "",
  purpose: "",
  premises: "",
  about: "",
  skills: "",
  experience: "",
  education: "",
});


const FIELDS_BY_TEMPLATE: Record<
  string,
  { partyALabel: string; partyBLabel: string; extras: Array<keyof Vars>; hidePartyB?: boolean }
> = {
  "service-agreement": { partyALabel: "Client (Party A)", partyBLabel: "Service Provider (Party B)", extras: ["amount", "startDate", "endDate", "reference"] },
  "employment-contract": { partyALabel: "Employee", partyBLabel: "Employer", extras: ["amount", "startDate"] },
  "nda-mutual": { partyALabel: "Party A", partyBLabel: "Party B", extras: ["purpose", "date"] },
  "lease-residential": { partyALabel: "Tenant", partyBLabel: "Landlord", extras: ["amount", "startDate", "endDate", "premises"] },
  "letter-of-demand": { partyALabel: "Creditor (you)", partyBLabel: "Debtor", extras: ["amount", "date", "cause", "reference"] },
  "acknowledgement-of-debt": { partyALabel: "Debtor", partyBLabel: "Creditor", extras: ["amount", "startDate", "cause", "reference"] },
  "popia-consent": { partyALabel: "Responsible Party (your organisation)", partyBLabel: "Data Subject", extras: ["date"] },
  "payment-arrangement": { partyALabel: "Debtor", partyBLabel: "Creditor", extras: ["amount", "startDate", "reference"] },
  "cv-classic-graduate": { partyALabel: "Candidate (you)", partyBLabel: "Employer", extras: ["purpose"], hidePartyB: true },
  "cv-professional-modern": { partyALabel: "Candidate (you)", partyBLabel: "Employer", extras: ["purpose"], hidePartyB: true },
  "cv-executive-premium": { partyALabel: "Candidate (you)", partyBLabel: "Employer", extras: ["purpose"], hidePartyB: true },
  "cv-legal-premium": { partyALabel: "Candidate (you)", partyBLabel: "Employer", extras: ["purpose"], hidePartyB: true },
  "cover-letter-general": { partyALabel: "Candidate (you)", partyBLabel: "Prospective employer", extras: ["purpose", "date"] },
  "cover-letter-graduate": { partyALabel: "Candidate (you)", partyBLabel: "Prospective employer", extras: ["purpose", "date"] },
  "cover-letter-executive": { partyALabel: "Candidate (you)", partyBLabel: "Prospective employer", extras: ["purpose", "date"] },
  "cover-letter-legal": { partyALabel: "Candidate (you)", partyBLabel: "Prospective employer", extras: ["purpose", "date"] },
};

const EXTRA_META: Record<
  string,
  { label: string; placeholder: string; type?: string; textarea?: boolean }
> = {
  amount: { label: "Amount (Rand)", placeholder: "e.g. 12 500.00" },
  startDate: { label: "Start date", placeholder: "e.g. 1 July 2026" },
  endDate: { label: "End date / notice", placeholder: "e.g. 30 June 2027" },
  date: { label: "Document date", placeholder: "e.g. 15 June 2026" },
  reference: { label: "Reference / invoice no.", placeholder: "e.g. INV-2026-001" },
  cause: { label: "Cause of debt / claim", placeholder: "e.g. unpaid invoice no. 2026/001 dated 1 March 2026", textarea: true },
  purpose: { label: "Purpose of disclosure", placeholder: "e.g. exploring a potential supply agreement", textarea: true },
  premises: { label: "Premises / address", placeholder: "e.g. 12 Elm Road, Polokwane, 0699" },
};

function ContractFormPage() {
  const { tpl } = Route.useLoaderData();
  const cfg = FIELDS_BY_TEMPLATE[tpl.slug] ?? {
    partyALabel: "Party A",
    partyBLabel: "Party B",
    extras: [],
  };
  const [vars, setVars] = useState<Vars>(empty);
  const [busy, setBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [attachIds, setAttachIds] = useState<string[]>([]);
  const createOrder = useServerFn(createContractOrder);

  async function onPayWithPayFast() {
    if (!isPremium(tpl.slug)) return;
    const name = vars.partyA.name.trim();
    const email = vars.partyA.email.trim();
    if (name.length < 2 || !/.+@.+\..+/.test(email)) {
      toast.error("Add your full name and email above", {
        description: "We need these to issue the invoice and email your receipt.",
      });
      return;
    }
    setPayBusy(true);
    try {
      const res = await createOrder({
        data: {
          slug: tpl.slug,
          clientName: name,
          clientEmail: email,
          clientPhone: vars.partyA.phone,
          vars,
        },
      });
      const reference = res.order.reference;
      toast.success("Redirecting to PayFast secure checkout…");
      const { launchPayfastCheckout } = await import("@/lib/payfast-launch");
      await launchPayfastCheckout(reference);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error("Could not start payment", { description: msg });
      setPayBusy(false);
    }
  }


  const fetchAttachments = useServerFn(listPublicCompanyAttachments);
  const { data: attData } = useQuery({
    queryKey: ["public-company-attachments"],
    queryFn: () => fetchAttachments(),
    staleTime: 60_000,
  });
  const attachments = attData?.attachments ?? [];

  function toggleAttach(id: string) {
    setAttachIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function setParty(side: "partyA" | "partyB", key: keyof Vars["partyA"], v: string) {
    setVars((prev) => ({ ...prev, [side]: { ...prev[side], [key]: v } }));
  }

  const navigate = useNavigate();
  const premium = isPremium(tpl.slug);
  const price = priceFor(tpl.slug);

  async function onGenerate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      // Premium contracts: payment is mandatory. Send the user to the
      // PayFast checkout flow — no AI-credit bypass for paid documents.
      if (premium) {
        toast.info("Premium contract — payment required", {
          description: "You'll be taken to checkout. The download unlocks automatically after PayFast confirms payment.",
        });
        navigate({ to: "/checkout" as any, search: { slug: tpl.slug } as any });
        return;
      }
      const res = await fetch("/api/public/contract-personalised", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug: tpl.slug, vars, attachIds }),
      });
      if (!res.ok) {
        const ct = res.headers.get("content-type") ?? "";
        if (ct.includes("application/json")) {
          const j = await res.json().catch(() => ({}));
          if (j.checkoutPath) {
            toast.error(j.message || "Payment required", { description: "Redirecting to checkout…" });
            navigate({ to: j.checkoutPath as any });
            return;
          }
          throw new Error(j.error || `Server returned ${res.status}`);
        }
        throw new Error(`Server returned ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (vars.partyA.name || vars.partyB.name || tpl.slug)
        .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
      a.download = `${tpl.slug}-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Contract generated", { description: "Check your downloads folder." });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error("Could not generate", { description: msg });
    } finally {
      setBusy(false);
    }

  }

  function downloadBlank() {
    window.location.href = `/api/public/contract?slug=${tpl.slug}`;
  }

  return (
    <div className="bg-background">
      <section className="bg-gradient-to-b from-navy/5 to-transparent py-12">
        <div className="container-narrow">
          <Link
            to="/contracts"
            className="inline-flex items-center gap-1 text-xs font-medium text-navy hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to contract library
          </Link>
          <div className="mt-4 flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-navy/10 text-navy">
              <FileSignature className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs uppercase tracking-[0.3em] text-gold">
                  {tpl.category}
                </p>
                <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${premium ? "bg-gold/20 text-amber-900" : "bg-emerald-600/15 text-emerald-800"}`}>
                  {premium ? `Premium · R${price}` : "Free"}
                </span>
              </div>
              <h1 className="mt-1 font-display text-3xl text-navy md:text-4xl">
                {tpl.title}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                {tpl.description}{" "}
                Fill in the fields below to produce a branded PDF carrying
                our logo and contact details. Blank fields stay as
                [BRACKETED] placeholders so you can hand-fill them later.
              </p>
            </div>
          </div>
          <div className="mt-5 max-w-3xl">
            <Disclaimer variant="compact" />
          </div>

          {/* Pricing & quality breakdown */}
          <div className="mt-6 max-w-3xl rounded-xl border border-navy/15 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                  {premium ? "Premium contract" : "Free template"} · {tpl.category}
                </div>
                <div className="mt-0.5 text-base font-semibold text-navy">
                  {tpl.title}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Final price
                </div>
                <div className="text-2xl font-bold text-navy">
                  {premium ? `R${price}` : "Free"}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {premium
                    ? "Once-off payment via PayFast"
                    : "Branded PDF · no credit required"}
                </div>
              </div>
            </div>

            <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
              <div className="flex justify-between gap-2 border-b border-dashed border-navy/10 py-1">
                <dt className="text-muted-foreground">Quality tier</dt>
                <dd className="font-medium text-navy">
                  {!premium
                    ? "Standard"
                    : price >= 500
                      ? "Corporate governance"
                      : price >= 299
                        ? "Regulated / advanced"
                        : "Standard commercial"}
                </dd>
              </div>
              <div className="flex justify-between gap-2 border-b border-dashed border-navy/10 py-1">
                <dt className="text-muted-foreground">South African law-aware</dt>
                <dd className="font-medium text-navy">Yes</dd>
              </div>
              <div className="flex justify-between gap-2 border-b border-dashed border-navy/10 py-1">
                <dt className="text-muted-foreground">Branded PDF output</dt>
                <dd className="font-medium text-navy">Yes</dd>
              </div>
              <div className="flex justify-between gap-2 border-b border-dashed border-navy/10 py-1">
                <dt className="text-muted-foreground">Editable placeholders</dt>
                <dd className="font-medium text-navy">Yes</dd>
              </div>
            </dl>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a
                href={`/api/public/contract?slug=${tpl.slug}&sample=1`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-navy/30 bg-white px-3 py-1.5 text-xs font-medium text-navy hover:bg-navy hover:text-white"
              >
                <Download className="h-3.5 w-3.5" />
                Download watermarked sample
              </a>
              <span className="text-[11px] text-muted-foreground">
                Previews are watermarked. Complete payment to unlock the clean branded PDF.
              </span>
            </div>
          </div>
        </div>
      </section>


      <section className="py-10">
        <div className="container-narrow">
          <form
            onSubmit={onGenerate}
            className="grid gap-6 rounded-2xl border border-border bg-card p-6 shadow-soft md:p-8"
          >
            <PartyFieldset
              heading={cfg.partyALabel}
              prefix="A"
              values={vars.partyA}
              onChange={(k, v) => setParty("partyA", k, v)}
            />
            {!cfg.hidePartyB && (
              <PartyFieldset
                heading={cfg.partyBLabel}
                prefix="B"
                values={vars.partyB}
                onChange={(k, v) => setParty("partyB", k, v)}
              />
            )}

            {cfg.extras.length > 0 && (
              <fieldset className="space-y-4">
                <legend className="font-display text-lg text-navy">
                  Contract details
                </legend>
                <div className="grid gap-4 sm:grid-cols-2">
                  {cfg.extras.map((k) => {
                    const meta = EXTRA_META[k as string];
                    if (!meta) return null;
                    const id = `extra-${k}`;
                    const isCareer = tpl.category === "Career";
                    const label =
                      isCareer && k === "purpose"
                        ? "Target role"
                        : isCareer && k === "date"
                          ? "Letter date"
                          : meta.label;
                    const placeholder =
                      isCareer && k === "purpose"
                        ? "e.g. Candidate Attorney, Compliance Officer"
                        : meta.placeholder;
                    return (
                      <div
                        key={k as string}
                        className={meta.textarea ? "sm:col-span-2" : ""}
                      >
                        <Label htmlFor={id}>{label}</Label>
                        {meta.textarea ? (
                          <Textarea
                            id={id}
                            value={vars[k] as string}
                            onChange={(e) =>
                              setVars((p) => ({ ...p, [k]: e.target.value }))
                            }
                            placeholder={placeholder}
                            maxLength={600}
                            className="mt-1"
                          />
                        ) : (
                          <Input
                            id={id}
                            value={vars[k] as string}
                            onChange={(e) =>
                              setVars((p) => ({ ...p, [k]: e.target.value }))
                            }
                            placeholder={placeholder}
                            maxLength={200}
                            className="mt-1"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </fieldset>
            )}

            {tpl.category === "Career" && (
              <fieldset className="space-y-4 rounded-xl border border-navy/15 bg-navy/[0.03] p-5">
                <legend className="px-2 font-display text-lg text-navy">
                  Your story{" "}
                  <span className="text-xs font-normal text-muted-foreground">
                    (optional — what you fill in replaces the sample text)
                  </span>
                </legend>
                <div>
                  <Label htmlFor="cv-about">Professional profile / About me</Label>
                  <Textarea
                    id="cv-about"
                    value={vars.about}
                    onChange={(e) => setVars((p) => ({ ...p, about: e.target.value }))}
                    rows={3}
                    maxLength={2000}
                    placeholder="2–4 sentences: who you are, what you're great at, and the role you're targeting."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="cv-skills">Core skills</Label>
                  <Textarea
                    id="cv-skills"
                    value={vars.skills}
                    onChange={(e) => setVars((p) => ({ ...p, skills: e.target.value }))}
                    rows={3}
                    maxLength={2000}
                    placeholder="Comma- or newline-separated. e.g. POPIA, contract drafting, MS 365, plain-language writing"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="cv-experience">Work experience</Label>
                  <Textarea
                    id="cv-experience"
                    value={vars.experience}
                    onChange={(e) =>
                      setVars((p) => ({ ...p, experience: e.target.value }))
                    }
                    rows={7}
                    maxLength={6000}
                    placeholder="One role per paragraph"
                    className="mt-1 font-mono text-xs"
                  />
                </div>
                <div>
                  <Label htmlFor="cv-education">Education & qualifications</Label>
                  <Textarea
                    id="cv-education"
                    value={vars.education}
                    onChange={(e) =>
                      setVars((p) => ({ ...p, education: e.target.value }))
                    }
                    rows={4}
                    maxLength={3000}
                    placeholder="LLB · University of South Africa · 2024"
                    className="mt-1 font-mono text-xs"
                  />
                </div>
              </fieldset>
            )}

            {attachments.length > 0 && (
              <fieldset className="space-y-3 rounded-xl border border-gold/30 bg-secondary/30 p-4">
                <legend className="px-2 font-display text-base text-navy">
                  Attach company documents
                </legend>
                <p className="text-xs text-muted-foreground">
                  Tick to append these PDFs to the end of your generated contract.
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {attachments.map((a: { id: string; label: string; kind: string }) => {
                    const id = `attach-${a.id}`;
                    const checked = attachIds.includes(a.id);
                    return (
                      <label
                        key={a.id}
                        htmlFor={id}
                        className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-card p-2 text-sm hover:bg-muted"
                      >
                        <Checkbox
                          id={id}
                          checked={checked}
                          onCheckedChange={() => toggleAttach(a.id)}
                          className="mt-0.5"
                        />
                        <span>
                          <span className="block font-medium text-navy">{a.label}</span>
                          <span className="block text-[11px] uppercase tracking-wide text-muted-foreground">
                            {a.kind}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            )}

            <div className="flex flex-wrap items-center gap-3 border-t border-border pt-5">
              {premium && (
                <Button
                  type="button"
                  onClick={onPayWithPayFast}
                  disabled={payBusy}
                  className="bg-gold text-navy hover:bg-gold/90"
                >
                  {payBusy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating invoice…
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Pay R{price} with PayFast
                    </>
                  )}
                </Button>
              )}
              <Button type="submit" disabled={busy} className="bg-navy hover:bg-navy/90">
                {busy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating PDF…
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    {premium ? `Pay R${price} & download` : "Generate & download (free)"}
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={downloadBlank}>
                Download blank template
              </Button>
              <p className="ml-auto text-xs text-muted-foreground">
                {premium
                  ? "PayFast takes card, Instant EFT, Capitec Pay & SnapScan. Receipt emailed automatically."
                  : "Your details are not stored — PDF is built in one request."}
              </p>
            </div>

          </form>
        </div>
      </section>
    </div>
  );
}

function PartyFieldset({
  heading,
  prefix,
  values,
  onChange,
}: {
  heading: string;
  prefix: string;
  values: Vars["partyA"];
  onChange: (k: keyof Vars["partyA"], v: string) => void;
}) {
  const fields: { k: keyof Vars["partyA"]; label: string; placeholder: string }[] = [
    { k: "name", label: "Full legal name", placeholder: "e.g. Acme (Pty) Ltd / Jane Doe" },
    { k: "idnum", label: "ID / Registration no.", placeholder: "e.g. 2010/123456/07 or 8001015009087" },
    { k: "address", label: "Physical address", placeholder: "Street, city, postal code" },
    { k: "email", label: "Email", placeholder: "name@example.co.za" },
    { k: "phone", label: "Phone", placeholder: "071 000 0000" },
  ];
  return (
    <fieldset className="space-y-4">
      <legend className="font-display text-lg text-navy">{heading}</legend>
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map((f) => {
          const id = `${prefix}-${f.k}`;
          return (
            <div key={f.k} className={f.k === "address" ? "sm:col-span-2" : ""}>
              <Label htmlFor={id}>{f.label}</Label>
              <Input
                id={id}
                value={values[f.k]}
                onChange={(e) => onChange(f.k, e.target.value)}
                placeholder={f.placeholder}
                maxLength={200}
                className="mt-1"
              />
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
